import { useSnackbar } from '@components/snackbar';
import { CloseIcon } from '@sketchnote/icons';
import {
  Button,
  FileUpload,
  MoreActionMenu,
  TemplateCategorySelectNoOptions,
  TemplateCategorySelectOption,
  TemplateCategorySelectSingleValue,
  TemplateCategorySelectStyles,
  UserSelectDropDownIndicator,
} from '@sketchnote/shared-components';
import update from 'immutability-helper';
import React, { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { API } from 'src/api';
import { ColumnToolbarLabel } from 'src/features/kanban/component/Column/Toolbar';
import { useAuth } from 'src/shared/hooks/use-auth';

import { TemplateDoc } from './types';

const ColourPicker = ({ onClick }: { onClick: (e: any) => void }) => (
  <MoreActionMenu
    usePortal
    onClickMenuItem={onClick}
    classNames={{
      menuWrap: 'rounded-lg shadow-sm',
    }}
    menuItems={[
      {
        label: (
          <ColumnToolbarLabel
            color="bg-gray-100"
            borderColor="border-gray-300"
            labelColor="Default"
          />
        ),
        value: '#F6F7F9',
      },
      {
        label: (
          <ColumnToolbarLabel
            color="bg-primary-100"
            borderColor="border-blue-300"
            labelColor="Blue"
          />
        ),
        value: '#EEF1FC',
      },
      {
        label: (
          <ColumnToolbarLabel
            color="bg-green-100"
            borderColor="border-green-300"
            labelColor="Green"
          />
        ),
        value: '#DFF8E3',
      },
      {
        label: (
          <ColumnToolbarLabel color="bg-red-100" borderColor="border-red-600" labelColor="Red" />
        ),
        value: '#FEEFED',
      },
      {
        label: (
          <ColumnToolbarLabel
            color="bg-yellow-100"
            borderColor="border-yellow-300"
            labelColor="Yellow"
          />
        ),
        value: '#FFF7D6',
      },
      {
        label: (
          <ColumnToolbarLabel color="bg-pink-100" borderColor="border-pink-300" labelColor="Pink" />
        ),
        value: '#fff5f7',
      },
      {
        label: (
          <ColumnToolbarLabel
            color="bg-purple-100"
            borderColor="border-purple-300"
            labelColor="Purple"
          />
        ),
        value: '#faf5ff',
      },
      {
        label: (
          <ColumnToolbarLabel
            color="bg-orange-200"
            borderColor="border-orange-300"
            labelColor="Orange"
          />
        ),
        value: '#fffaf0',
      },
    ]}
  />
);

export const SaveTemplate = ({
  fileId,
  onClose,
  templateData,
  mode = 'add',
}: {
  fileId: string;
  templateData?: TemplateDoc;
  onClose: () => void;
  mode?: 'edit' | 'add';
}) => {
  // const [headerImage, setHeaderImage] = useState<string>('');
  // const [boardImage, setBoardImage] = useState<string>('');
  // const [docImage, setDocImage] = useState<string>('');
  const initialValues = {
    file: fileId,
    title: '',
    synopsis: '',
    category: '',
    description: '',
    images: [],
    templateType: [],
    color: '#F6F7F9',
  };
  const [values, setValues] = useState<any>({});
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [openSnackbar] = useSnackbar();
  const authData = useAuth((s) => s.userDetails);
  const orgId = authData?.memberInformation?.organisation?.organisationId;
  const [isCommunityTemplate, setIsCommunityTemplate] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const isSketchnoteMember =
    authData?.email.endsWith('sketchnote.co') || authData?.email === 'placebonikhil@gmail.com';

  useEffect(() => {
    if (templateData) {
      setValues(templateData);
    } else {
      setValues(initialValues);
    }
  }, [templateData]);

  const fetchAllCategories = async () => {
    const categoryParams = isCommunityTemplate ? {} : { params: { org: orgId } };
    const categoriesResponse = await API.get(`/files/category`, categoryParams);

    if (categoriesResponse.data.success) {
      const catOptions = categoriesResponse.data.data.map((cat) => ({
        label: cat.title,
        value: cat._id,
      }));
      setCategoryOptions(catOptions);
    }
  };

  useEffect(() => {
    fetchAllCategories();
  }, [isCommunityTemplate]);

  const handleFileUpload = (files, type) => {
    files.forEach(async (file) => {
      const data = new FormData();

      data.append('file', file);

      const response = await API.post('/creation/file', data, {
        headers: { ['Content-Type']: 'multipart/form-data' },
      });

      if (response.data?.data?.message) {
        setValues(
          update(values, {
            images: { $push: [{ type: type, src: response.data?.data?.message.url }] },
          })
        );
      }
    });
  };

  const handleHeaderUpload = (files) => handleFileUpload(files, 'header');

  const onPickColor = (e) => {
    setValues(
      update(values, {
        color: { $set: e.value },
      })
    );
  };

  const handleTitleChange = (e) => {
    setValues(
      update(values, {
        title: { $set: e.target.value },
      })
    );
  };

  const handleSynopsisChange = (e) => {
    setValues(
      update(values, {
        synopsis: { $set: e.target.value },
      })
    );
  };

  const handleDescriptionChange = (e) => {
    setValues(
      update(values, {
        description: { $set: e.target.value },
      })
    );
  };

  // const handleDescriptionChange = (e: any[]) => {
  //   setValues(update(values, { description: { $set: e } }));
  // };

  const handleChangeCategory: any = async (options, actions) => {
    if (actions.action === 'select-option') {
      setValues(update(values, { category: { $set: options.value } }));
    } else if (actions.action === 'create-option') {
      const categoryResponseParams = isCommunityTemplate
        ? { title: options.label }
        : { title: options.label, org: orgId };
      const categoryRespose = await API.post('/files/category', categoryResponseParams);

      if (categoryRespose.data.success) {
        setValues(update(values, { category: { $set: categoryRespose.data.data._id } }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitDisabled(true);
    try {
      let response;
      const saveTemplateParams = isCommunityTemplate ? { ...values } : { ...values, org: orgId };
      if (mode === 'add') {
        response = await API.post('/files/template', saveTemplateParams);
      } else {
        response = await API.put('/files/template', {
          id: templateData?._id,
          ...saveTemplateParams,
        });
      }
      if (response.data.success) {
        openSnackbar({
          text: 'Template successfully saved',
          snackType: 'standard',
          position: 'bottom-center',
        });
      } else {
        openSnackbar({
          text: 'There was a problem saving the template',
          snackType: 'standard',
          position: 'bottom-center',
        });
        setIsSubmitDisabled(false);
      }
      onClose();
    } catch (error) {
      setIsSubmitDisabled(false);
      openSnackbar({
        text: 'Error',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };

  useEffect(() => {
    if (
      values?.title?.length < 1 ||
      values?.synopsis?.length < 1 ||
      values?.category?.length < 1 ||
      values?.description?.length < 1
    ) {
      setIsSubmitDisabled(true);
    } else {
      setIsSubmitDisabled(false);
    }
  }, [values]);

  return (
    <div className="min-w-900 rounded-sm">
      <header className="flex justify-between align-middle p-20 border-b">
        <div className="font-medium flex items-center">
          <span>Save as template</span>
        </div>
        <div>
          <CloseIcon className="text-xs" onClick={onClose} />
        </div>
      </header>
      <main className="flex">
        <section className="border-l h-full w-1076 rounded-br-xl max-h-600 overflow-y-scroll overflow-x-hidden">
          <div>
            <header
              className="h-200 w-full overflow-hidden relative"
              style={{ backgroundColor: values.color }}
            >
              <div className="absolute top-12 right-20 z-10">
                <ColourPicker onClick={onPickColor} />
              </div>
              {values?.images?.length > 0 && values?.images?.some((i) => i.type === 'header') ? (
                <div className="h-200 w-400 overflow-hidden m-auto">
                  <img
                    src={values.images.find((i) => i.type === 'header').src}
                    alt="placeholder"
                    className="w-full object-center object-contain h-full"
                  />
                  {/* <CloseIcon className="absolute top-0 right-0" /> */}
                </div>
              ) : (
                <>
                  <FileUpload
                    className="mb-8 w-356 absolute left-120 top-50"
                    translations={{
                      attachments_drop_label: 'Add Template Thumbnail',
                      attachments_label: 'Add Template Thumbnail',
                    }}
                    onDrop={handleHeaderUpload}
                    maxFileSize={5 * 1024 * 1024}
                    multiple={false}
                  />
                  <small className="absolute right-10 bottom-10 text-gray-500 text-justify">
                    <span>Ideal size: 400x200</span>
                    <br />
                    <span>Max File Size: 5 MB</span>
                  </small>
                </>
              )}
            </header>

            <main className="p-32">
              {isSketchnoteMember && (
                <section className="w-full flex justify-between mb-24">
                  <div className="w-full mr-30 flex items-center">
                    <input
                      type="checkbox"
                      checked={isCommunityTemplate}
                      onChange={() => setIsCommunityTemplate(!isCommunityTemplate)}
                      className="mr-16"
                    />
                    <label className="text-xs block text-gray-999" htmlFor="assigner">
                      Is a community template? (Only for Sketchnote Members)
                    </label>
                  </div>
                </section>
              )}
              <section className="w-full flex justify-between mb-24">
                <div className="w-full mr-30">
                  <label className="mb-14 block text-gray-999 font-medium" htmlFor="assigner">
                    Template title <sup className="text-red-600">*</sup>
                  </label>
                  <input
                    type="text"
                    value={values?.title}
                    onChange={handleTitleChange}
                    placeholder="Type template title here"
                    className="border w-full text-base placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-13 bg-gray-100 rounded-lg placeholder-text-base"
                  />
                  {/* <Plate
                    id={titleEditorId}
                    value={values.title}
                    onChange={handleTitleChange}
                    editableProps={{
                      autoFocus: true,
                      placeholder: 'Type the template title',
                      className: cn({
                        'border w-full text-base mt-18 placeholder-red-600 placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-13 bg-gray-100 rounded-lg placeholder-text-base':
                          true,
                      }),
                    }}
                  /> */}
                </div>
              </section>
              <section className="w-full flex justify-between mb-24">
                <div className="w-full mr-30">
                  <label className="mb-14 block text-gray-999 font-medium" htmlFor="assigner">
                    Synopsis <sup className="text-red-600">*</sup>
                  </label>
                  <input
                    type="text"
                    value={values.synopsis}
                    onChange={handleSynopsisChange}
                    placeholder="Type a short description of the template"
                    className="border w-full text-base placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-13 bg-gray-100 rounded-lg placeholder-text-base"
                  />
                  {/* <Plate
                    id={titleEditorId}
                    value={values.synopsis}
                    onChange={handleTitleChange}
                    editableProps={{
                      autoFocus: true,
                      placeholder: 'Type a short description of the template',
                      className: cn({
                        'border w-full text-base mt-18 placeholder-red-600 placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-13 bg-gray-100 rounded-lg placeholder-text-base':
                          true,
                      }),
                    }}
                  /> */}
                </div>
              </section>
              <section className="w-full flex justify-between mb-24">
                <div className="w-full mr-30">
                  <label className="mb-14 block text-gray-999 font-medium" htmlFor="assigner">
                    Category tag <sup className="text-red-600">*</sup>
                  </label>
                  <CreatableSelect
                    isClearable
                    onChange={handleChangeCategory}
                    menuPortalTarget={document.body}
                    placeholder="Select label"
                    className="mt-14"
                    options={[
                      {
                        label: 'Select an option or create one',
                        options: categoryOptions,
                      },
                    ]}
                    components={{
                      DropdownIndicator: UserSelectDropDownIndicator,
                      IndicatorSeparator: () => null,
                      NoOptionsMessage: TemplateCategorySelectNoOptions,
                      SingleValue: TemplateCategorySelectSingleValue,
                      Option: TemplateCategorySelectOption,
                    }}
                    styles={TemplateCategorySelectStyles}
                  />
                  {/* <Select
                    options={[
                      {
                        label: 'Select category',
                        options: categoryOptions,
                      },
                    ]}
                    value={values.category}
                    onChange={handleChangeCategory}
                    menuPortalTarget={document.body}
                    styles={TemplateCategorySelectStyles}
                    menuPlacement="bottom"
                    placeholder="Choose category"
                    className="mt-14"
                    components={{
                      DropdownIndicator: UserSelectDropDownIndicator,
                      IndicatorSeparator: () => null,
                      SingleValue: TemplateCategorySelectSingleValue,
                      Option: TemplateCategorySelectOption,
                    }}
                  /> */}
                </div>
              </section>
              <section className="w-full flex justify-between mb-24">
                <div className="w-full mr-30">
                  <label className="mb-14 block text-gray-999 font-medium" htmlFor="assigner">
                    Description <sup className="text-red-600">*</sup>
                  </label>
                  <input
                    type="text"
                    value={values.description}
                    onChange={handleDescriptionChange}
                    placeholder="Add a description"
                    className="border w-full text-base placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-13 bg-gray-100 rounded-lg placeholder-text-base"
                  />
                  {/* <MinimalEditor
                    editorValue={values.description}
                    id={descriptionEditorId}
                    onEditorChange={handleChangeDescription}
                    editableProps={{
                      autoFocus: false,
                      placeholder: 'Add a description',
                      className: cn({
                        'border border-gray-200 bg-gray-100 rounded p-10 mt-8 min-h-107 w-full':
                          true,
                      }),
                    }}
                  /> */}
                </div>
              </section>
            </main>
            <footer className="flex py-24 px-32 justify-end border-t">
              <Button disabled={isSubmitDisabled} onClick={handleSubmit}>
                Save as template
              </Button>
            </footer>
          </div>
        </section>
      </main>
    </div>
  );
};
